import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Home, CalendarCheck, Users, BookOpen, User, Bell, Globe, Shield } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { useI18n } from "../i18n/I18nContext";
import { LANGS } from "../i18n/strings";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import brand from "../config/brand.json";

export function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2" data-testid="brand-logo-link" aria-label={`${brand.name} home`}>
      <span className="grid h-9 w-9 place-items-center rounded-2xl bg-ckb-forest font-display text-lg font-bold text-ckb-sand">{brand.logoMark}</span>
      <span className="font-display text-lg font-bold tracking-tight text-ckb-ink">{brand.name}</span>
    </Link>
  );
}

export function LanguageSelect({ compact = false, testId = "language-selector-dropdown" }) {
  const { lang, setLang, t } = useI18n();
  const { user } = useAuth();
  const change = (code) => {
    setLang(code);
    if (user) api.patch("/profile", { language: code }).catch(() => {});
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid={testId} aria-label={t("language")} className="flex min-h-[44px] items-center gap-1.5 rounded-full border border-ckb-line bg-white px-3 text-sm text-ckb-ink2 hover:bg-ckb-mint">
        <Globe className="h-4 w-4" aria-hidden />
        {!compact && <span>{LANGS.find((l) => l.code === lang)?.label}</span>}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {LANGS.map((l) => (
          <DropdownMenuItem key={l.code} onClick={() => change(l.code)} data-testid={`language-option-${l.code}`}>{l.label}{l.code === lang ? " ✓" : ""}</DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NotificationBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const navigate = useNavigate();
  useEffect(() => { api.get("/notifications").then((r) => setData(r.data)).catch(() => {}); }, []);
  const open = (o) => { if (o && data.unread) api.post("/notifications/read").then(() => setData((d) => ({ ...d, unread: 0 }))).catch(() => {}); };
  return (
    <DropdownMenu onOpenChange={open}>
      <DropdownMenuTrigger data-testid="notifications-bell" aria-label={`Notifications${data.unread ? `, ${data.unread} unread` : ""}`} className="relative grid h-11 w-11 place-items-center rounded-full border border-ckb-line bg-white hover:bg-ckb-mint">
        <Bell className="h-4 w-4 text-ckb-ink2" />
        {data.unread > 0 && <span className="absolute -right-0.5 -top-0.5 rounded-full bg-ckb-ochre px-1.5 text-[10px] font-bold text-white" data-testid="notifications-unread-count">{data.unread}</span>}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        {data.items.length === 0 && <div className="p-3 text-sm text-ckb-muted">No notifications yet.</div>}
        {data.items.slice(0, 8).map((n) => (
          <DropdownMenuItem key={n.id} onClick={() => navigate(n.link)} className="text-sm">{n.text}</DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const linkCls = ({ isActive }) => `rounded-full px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-ckb-mint text-ckb-forest" : "text-ckb-ink2 hover:text-ckb-forest"}`;

export function Header() {
  const { t } = useI18n();
  const { user } = useAuth();
  const links = user
    ? [["/dashboard", t("nav_home")], ["/my-program", t("nav_my_program")], ["/community", t("nav_community")], ["/learn", t("nav_learn")], ["/progress", t("nav_progress")], ["/ai-coach", t("nav_coach")], ["/profile", t("nav_profile")]]
    : [["/", t("nav_home")], ["/programs", t("nav_programs")], ["/community", t("nav_community")], ["/learn", t("nav_learn")], ["/about", t("nav_about")]];
  return (
    <header className="sticky top-0 z-40 border-b border-ckb-line/70 bg-ckb-sand/85 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
        <Logo />
        <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
          {links.map(([to, label]) => <NavLink key={to} to={to} end={to === "/"} className={linkCls} data-testid={`nav-${to.replace(/\//g, "") || "home"}-link`}>{label}</NavLink>)}
          {user?.role === "ADMIN" && <NavLink to="/admin" className={linkCls} data-testid="nav-admin-link"><Shield className="inline h-4 w-4" /> Admin</NavLink>}
        </nav>
        <div className="flex items-center gap-2">
          <LanguageSelect compact />
          {user ? <NotificationBell /> : user === false && (
            <>
              <Link to="/login" className="flex min-h-[44px] items-center whitespace-nowrap px-2 text-sm font-medium text-ckb-forest sm:px-3" data-testid="header-sign-in-link">{t("sign_in")}</Link>
              <Link to="/assessment" className="ckb-btn-primary !hidden !min-h-[44px] !px-4 text-sm sm:!inline-flex" data-testid="header-assessment-cta">{t("take_assessment")}</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

export function BottomNav() {
  const { t } = useI18n();
  const { user } = useAuth();
  const items = [
    [user ? "/dashboard" : "/", Home, t("nav_home"), "home"],
    [user ? "/my-program" : "/programs", CalendarCheck, t("nav_program"), "program"],
    ["/community", Users, t("nav_community"), "community"],
    ["/learn", BookOpen, t("nav_learn"), "learn"],
    [user ? "/profile" : "/login", User, t("nav_profile"), "profile"],
  ];
  return (
    <nav className="fixed inset-x-0 bottom-0 z-50 border-t border-ckb-line bg-white/90 pb-[env(safe-area-inset-bottom)] backdrop-blur-md lg:hidden" aria-label="Bottom">
      <div className="grid grid-cols-5">
        {items.map(([to, Icon, label, id]) => (
          <NavLink key={id} to={to} end={to === "/"} data-testid={`bottom-nav-${id}`} className={({ isActive }) => `flex min-h-[60px] flex-col items-center justify-center gap-1 text-[11px] font-medium ${isActive ? "text-ckb-forest" : "text-ckb-muted"}`}>
            <Icon className="h-5 w-5" aria-hidden />
            {label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

export function Footer() {
  return (
    <footer className="mt-16 border-t border-ckb-line bg-ckb-sand2/60 pb-24 lg:pb-8">
      <div className="mx-auto grid max-w-6xl gap-8 px-4 py-10 sm:px-6 md:grid-cols-3">
        <div className="space-y-3">
          <Logo />
          <p className="max-w-xs text-sm text-ckb-ink2">{brand.tagline} {brand.positioning}</p>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-ckb-ink2 md:col-span-2 md:justify-end">
          {[["/about", "About"], ["/programs", "Programs"], ["/learn", "Learn"], ["/community", "Community"], ["/privacy", "Privacy"], ["/terms", "Terms"], ["/grievance", "Grievance"], ["/medical-disclaimer", "Medical Disclaimer"]].map(([to, l]) => (
            <Link key={to} to={to} className="hover:text-ckb-forest" data-testid={`footer-${l.toLowerCase().replace(/ /g, "-")}-link`}>{l}</Link>
          ))}
        </div>
        <p className="text-xs leading-relaxed text-ckb-muted md:col-span-3">
          {brand.name} provides general wellness education and lifestyle support. It does not provide medical diagnosis, treatment, or a substitute for care from a qualified healthcare professional. In an emergency call 112. For users in India only (18+). Grievance Officer: {brand.legal.officer}, <a href={`mailto:${brand.legal.grievanceEmail}`} className="underline" data-testid="footer-grievance-email">{brand.legal.grievanceEmail}</a>.
        </p>
      </div>
    </footer>
  );
}
